import { Component, OnInit } from '@angular/core';
import { FormArray } from '@angular/forms';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';

import { IContractArrayInput } from 'src/app/models/contract';
import { IBeneficiary, IGenericContract } from 'src/app/models/generic';
import { ApiContractService } from 'src/app/services/api/api-contract.service';
import { StatusBadgeTone } from 'src/app/shared/components/status-badge/status-badge.component';
import { DocumentValidator } from 'src/app/shared/validators/document.validator';

import {
    ContratoBeneficiarioFormComponent
} from '../contrato-beneficiario-form/contrato-beneficiario-form.component';
import {
    ContratoEditarComissaoComponent
} from '../contrato-editar-comissao/contrato-editar-comissao.component';
import {
    ContratoEditarValorCredimorarComponent
} from '../contrato-editar-valor-credimorar/contrato-editar-valor-credimorar.component';

@Component({
  selector: 'app-contrato-beneficiario',
  templateUrl: './contrato-beneficiario.component.html',
  styleUrls: ['./contrato-beneficiario.component.scss']
})
export class ContratoBeneficiarioComponent implements OnInit {
  beneficiariosFromResolver: IContractArrayInput[] = [];
  contractData!: IGenericContract;
  form!: FormArray;
  tabTitle = 'beneficiario';
  readonly!: boolean;

  constructor(
    private _dialog: MatDialog,
    private _activatedRoute: ActivatedRoute,
    public contractService: ApiContractService
  ) {
    const routeSub = _activatedRoute.params.subscribe((params) => {
      const currentMode = params['mode'];
      this.readonly = currentMode === 'visualizar';
    });
  }

  ngOnInit(): void {
    this.getDataFromResolver();
  }

  getDataFromResolver(): void {
    this._activatedRoute.parent?.data.subscribe({
      next: (data) => {
        this.beneficiariosFromResolver = data['contracts'][`${this.tabTitle}`];

        this.getContractDataAndBuildForm();
      }
    });
  }

  getContractDataAndBuildForm(): void {
    this.form = this.formFromService;
    this.contractData = this.contractDataFromService;
  }

  get formFromService() {
    return this.contractService.getForm()?.controls.Beneficiaries;
  }

  get contractDataFromService() {
    return this.contractService.contractData;
  }

  // O array bruto contractData.Beneficiaries não tem o campo Amount do beneficiário
  // Parceiro preenchido (só o da Subsidiária, via efeito colateral do getter
  // ApiContractService.subsidiary). O valor do Parceiro só existe depois de mesclado
  // no FormArray a partir de Transfer_Transfer (ApiContractService.handleBeneficiaries).
  // Por isso a view somente-leitura lê do form, não do contractData.Beneficiaries direto.
  get beneficiariesList(): IBeneficiary[] {
    return this.form?.getRawValue() || [];
  }

  get totalBeneficiaries(): number {
    return this.beneficiariesList.length;
  }

  get totalCommissionPercent(): number {
    return this.beneficiariesList.reduce((sum, b) => sum + (b.Fee || 0), 0);
  }

  get totalAmountToReceive(): number {
    return this.beneficiariesList.reduce((sum, b) => sum + (b.Amount || 0), 0);
  }

  formatDocument(document?: string): string {
    return document ? DocumentValidator.formatCpfCnpj(document) : '-';
  }

  isCredimorar(beneficiario: IBeneficiary): boolean {
    return (beneficiario.Name || '').toUpperCase() === 'CREDIMORAR';
  }

  isParceiro(beneficiario: IBeneficiary): boolean {
    return (beneficiario.BeneficiaryType || '').toLowerCase() === 'parceiro';
  }

  // O tipo vem do banco sem acento (ex.: "Subsidiaria"); a tela exibe a grafia correta.
  beneficiaryTypeLabel(beneficiario: IBeneficiary): string {
    const type = beneficiario.BeneficiaryType || '';
    const labels: Record<string, string> = {
      subsidiaria: 'Subsidiária',
      imobiliaria: 'Imobiliária'
    };

    return labels[type.toLowerCase()] ?? (type || '-');
  }

  beneficiaryTone(beneficiario: IBeneficiary): StatusBadgeTone {
    const type = (beneficiario.BeneficiaryType || '').toLowerCase();

    if (type === 'subsidiaria') return 'info';
    if (type === 'parceiro') return 'warning';

    return 'neutral';
  }

  get canEditPartnerCommission(): boolean {
    const channel = (this.contractData?.Chanel || '')
      .toLowerCase()
      .trim()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '');

    return this.readonly && channel !== 'indicacao';
  }

  handleBeneficiarioModal(): void {
    const dialogRef = this._dialog.open(ContratoBeneficiarioFormComponent, {
      disableClose: true,
      data: { contractId: this.contractData.ContractId },
      ariaLabel: 'Adicionar beneficiário'
    });

    dialogRef.afterClosed().subscribe({
      next: (res) => {
        if (res) {
          this.contractService.addNewGroupToFormArray(res);
        }
      }
    });
  }

  openDialogCommissionEdit(): void {
    if (!this.canEditPartnerCommission) {
      return;
    }

    const hasSubstitutedBeneficiary = this.contractData.Beneficiaries.some(x =>
      x.BeneficiaryType.toLowerCase() === 'subestabelecido');
    const dialogRef = this._dialog.open(ContratoEditarComissaoComponent, {
      id: 'contrato-editar-comissao-dialog',
      width: '420px',
      hasBackdrop: true,
      disableClose: false,
      ariaLabel: 'Editar comissão do beneficiário',
      data: {
        contractId: this.contractData.ContractId,
        justificationCommission: this.contractData.JustificationChangeCommission,
        hasSubstitutedBeneficiary
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('O dialog de alteração de comissão foi fechado');
      }
    });
  }

  openDialogCredimorarValueEdit(): void {

    const credimorar = this.contractData.Beneficiaries.find((b) => b.Name == 'CREDIMORAR');

    if(credimorar == null)
      return;

    const dialogRef = this._dialog.open(ContratoEditarValorCredimorarComponent, {
      id: 'contrato-editar-valorcredimorar-dialog',
      width: '420px',
      hasBackdrop: true,
      disableClose: false,
      ariaLabel: 'Editar valor Credimorar',
      data: {
        contractId: this.contractData.ContractId,
        beneficiaryId: credimorar.BeneficiaryId,
        amount: credimorar.Amount
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('O dialog de alteração de valor credimorar foi fechado');
      }
    });
  }
}
